import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
// Wrapped Routes

import { APRoutePaths, ARRoutePaths, EPaths } from '@v2/constants/EPaths';
import './stylesheets/main.scss';

import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { ArPageLayout } from '@ar/layouts/ArPageLayout';
import { PayoutsWrapperPage } from '@ar/pages/Payouts/PayoutsWrapper';
import { PayoutsPage } from '@ar/pages/PayoutsPage/PayoutsPage';
import { PayoutDetailsPage } from '@ar/pages/PayoutDetailsPage/PayoutDetailsPage';
import { CustomerDetailsWrapperPage } from '@ar/pages/CustomerDetailsWrapperPage/CustomerDetailsWrapperPage';
import { CustomersPage } from '@ar/pages/CustomersPage/CustomersPage';
import { CustomerDetailsPage } from '@ar/pages/CustomerDetailsPage/CustomerDetailsPage';
import { InvoicesWrapperPage } from '@ar/pages/Invoices/InvoicesWrapper';
import { InvoicesPage } from '@ar/pages/InvoicesPage/InvoicesPage';
import { InvoiceDetailsPage } from '@ar/pages/InvoiceDetailsPage/InvoiceDetailsPage';
import { SettingsPage } from '@ar/pages/SettingsPage/SettingsPage';
import { BrandingPage } from '@ar/pages/BrandingPage/BrandingPage';
import { BankingPage } from '@ar/pages/BankingPage/BankingPage';
import { IntegrationsWrapperPage } from '@ar/pages/IntegrationsPage/IntegrationsWrapperPage';
import { setupAxiosAuth } from '@v2/utils/AxiosUtils';
import { ApPageLayout } from '@ap/layouts/ApPageLayout';
import { useSupplierStore } from '@ar/stores/SupplierStore';
import { SignUpPage } from '@ar/pages/SignUpPage/SignUpPage';
import { BillDetailsPage } from '@ap/pages/BillDetailsPage/BillDetailsPage';
import { getCustomerContext,
    getSupplierContext,
    LaunchDarklyContextKind,
    getLoginContext,
    setupLaunchDarkly } from '@v2/utils/LaunchdarklyUtils';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { CustomerNotificationsPage } from '@ar/pages/NotificationsPageV2/CustomerNotificationsPage';
import { CustomerSignUpPage, SIGN_UP_SUPPLIER_ID_KEY } from '@ar/pages/SignUpPage/CustomerSignUpPage';
import { ApBillDetailsPage } from '@ar/pages/ApBillDetailsPage/ApBillDetailsPage';
import { ApBillsPage } from '@ar/pages/ApBillsPage/ApBillsPage';
import { initSupertokens } from '@v2/ext/supertokens';
import Session from 'supertokens-web-js/recipe/session';
import { UserRoleClaim } from 'supertokens-web-js/recipe/userroles';
import { UserAuthenticationProvider } from '@/auth/UserAuthenticationProvider';
import { BillsPage } from '@/ap/pages/BillsPage/BillsPage';
import { SuppliersPage as ApSuppliersPage } from '@/ap/pages/SuppliersPage/SuppliersPage';
import AppWrapper from '@/containers/app/AppWrapper';
import SignInContainer from '@/containers/signin/SignInContainer';
import { Currency } from '@/ar/network/AccountsReceivable.network';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { SupplierNotificationsPage } from './ar/pages/NotificationPage/SupplierNotificationsPage';
import { CustomerSettingsPage } from '@/ap/pages/SettingsPage/CustomerSettingsPage';
import { EmailInvoicePDFPage } from './pages/SendInvoicePDFEmailPage';
import { DraftInvoicesPage } from './ar/pages/DraftInvoicesPage/DraftInvoicesPage';
import { DraftInvoiceDetailsPage } from './ar/pages/DraftInvoiceDetailsPage/DraftInvoiceDetailsPage';
import { AcceptMandatePage } from './pages/AcceptMandatePage';
import { MaintenanceMode } from './ar/components/shared/MaintenanceMode/MaintenanceMode';
import { FeatureFlags } from './constants/FeatureFlags';
import { useGetCustomerSupplierMappings } from '@/ar/hooks/queries/CustomerQueries.hook';
import { SupplierHomePage } from './ar/pages/SupplierHomePage/SupplierHomePage';
import { PasswordlessVerifyPage } from './pages/supertokens/PasswordlessVerifyPage';
import { MagicLinkRequestPage } from './ap/pages/MagicLinkRequestPage/MagicLinkRequestPage';
import { AdyenCapitalGrantsPage } from './ar/pages/AdyenCapitalGrantsPage/AdyenCapitalGrantsPage';

const PublicRoutes = (): JSX.Element => (
    <BrowserRouter>
        <Routes>
            <Route index element={<Navigate to={EPaths.SIGN_IN}/>}/>
            <Route path="/" element={<AppWrapper/>}>
                <Route path="*" element={<Navigate to={EPaths.SIGN_IN}/>}/>
                <Route path={EPaths.SIGN_IN} element={<SignInContainer/>}/>
                <Route path={EPaths.SIGN_UP} element={<SignUpPage/>}/>
                <Route path={EPaths.FORGOT_PASSWORD} element={<ForgotPasswordPage/>}/>
                <Route path={EPaths.RESET_PASSWORD} element={<ResetPasswordPage/>}/>
                <Route path={EPaths.CUSTOMERS_SIGN_UP} element={<CustomerSignUpPage/>}/>
                <Route path={EPaths.EMAIL_INVOICE_PDF} element={<EmailInvoicePDFPage/>}/>
                <Route path={EPaths.ACCEPT_MANDATE} element={<AcceptMandatePage/>}/>
                <Route path={EPaths.SUPERTOKENS_VERIFY} element={<PasswordlessVerifyPage />} />
                <Route path={EPaths.MAGIC_LINK_REQUEST} element={<MagicLinkRequestPage/>}/>
            </Route>
        </Routes>

    </BrowserRouter>
);

const BillRoutes = <Route path={ARRoutePaths.AR_BILLS} element={<InvoicesWrapperPage/>}>
    <Route index element={<BillsPage/>}/>
    <Route path=":invoiceID" element={<BillDetailsPage/>}/>
</Route>;

async function getStripeClient(currency: Currency | undefined) {
    const isUSD = currency === Currency.usd;

    return await loadStripe(
        isUSD ? process.env.STRIPE_US_API_KEY : process.env.STRIPE_API_KEY
    );
}

const APRoutes = (): JSX.Element => {
    const { supplierLoginData } = useSupplierStore.getState();
    const stripePromise = getStripeClient(supplierLoginData?.currency);

    return <Elements stripe={stripePromise}>
        <BrowserRouter>
            <Routes>
                <Route path="*" element={<Navigate to={`/${ARRoutePaths.AR_BILLS}`}/>}/>
                <Route path="" element={<ApPageLayout/>}>
                    <Route index element={<Navigate to={`/${ARRoutePaths.AR_BILLS}`}/>}/>
                    {BillRoutes}
                    <Route path={APRoutePaths.AP_SUPPLIERS} element={<ApSuppliersPage/>}/>
                    <Route path={APRoutePaths.AP_SETTINGS} element={<CustomerSettingsPage/>}/>
                    <Route path={APRoutePaths.AP_BILLS} element={<InvoicesWrapperPage/>}>
                        <Route index element={<ApBillsPage/>}/>
                        <Route path=":billID" element={<ApBillDetailsPage/>}/>
                    </Route>
                    <Route path={EPaths.ACCEPT_MANDATE} element={<AcceptMandatePage className="px-0" />}/>
                    {/* allow SuperTokens to replace existing session with a new one */}
                    <Route path={EPaths.SUPERTOKENS_VERIFY} element={<PasswordlessVerifyPage />} />
                    {/* <Route path={APRoutePaths.AP_PAYMENT_METHODS} element={<ApPaymentMethodsPage/>}/> */}
                    {/* <Route path={ARRoutePaths.AR_SETTINGS} element={<SettingsPage/>}/> */}
                    {/* <Route path={ARRoutePaths.AR_BRANDING} element={<BrandingPage/>}/> */}
                    {/* <Route path={ARRoutePaths.AR_BANKING} element={<BankingPage/>}/> */}
                    {/* <Route path={ARRoutePaths.AR_INTEGRATION} element={<IntegrationsWrapperPage/>}/> */}
                </Route>
            </Routes>
        </BrowserRouter>
    </Elements>;
};

const ARRoutes = (): JSX.Element => {
    const { supplierLoginData } = useSupplierStore.getState();
    const stripePromise = getStripeClient(supplierLoginData?.currency);

    const isCustomerAndSupplier = useSupplierStore.getState().supplierLoginData?.isCustomerAndSupplier;
    return <Elements stripe={stripePromise}>
        <BrowserRouter>
            <Routes>
                <Route path="*" element={<Navigate to={`/${ARRoutePaths.AR_INVOICES}`}/>}/>
                <Route path="" element={<ArPageLayout/>}>
                    <Route index element={<Navigate to={`/${ARRoutePaths.AR_INVOICES}`}/>}/>
                    <Route path={ARRoutePaths.AR_PAYOUTS} element={<PayoutsWrapperPage/>}>
                        <Route index element={<PayoutsPage/>}/>
                        <Route path=":payoutID" element={<PayoutDetailsPage/>}/>
                    </Route>
                    <Route element={<CustomerDetailsWrapperPage/>}>
                        <Route path={ARRoutePaths.AR_CUSTOMERS} element={<CustomersPage/>}/>
                        <Route path={`${ARRoutePaths.AR_CUSTOMERS}/:customerID`} element={<CustomerDetailsPage/>}/>
                    </Route>
                    <Route path={ARRoutePaths.AR_INVOICES} element={<InvoicesWrapperPage/>}>
                        <Route index element={<InvoicesPage/>}/>
                        <Route path=":invoiceID" element={<InvoiceDetailsPage/>}/>
                    </Route>
                    <Route path={ARRoutePaths.AR_DRAFT_INVOICES} element={<InvoicesWrapperPage/>}>
                        <Route index element={<DraftInvoicesPage/>}/>
                        <Route path=":invoiceID" element={<DraftInvoiceDetailsPage/>}/>
                    </Route>
                    <Route path={ARRoutePaths.AR_BILLS} element={<InvoicesWrapperPage/>}>
                        <Route index element={<BillsPage/>}/>
                        {/* <Route path=":invoiceID" element={<InvoiceDetailsPage/>}/> */}
                    </Route>
                    <Route path={ARRoutePaths.AR_SUPPLIER_HOME} element={<SupplierHomePage/>} />
                    <Route path={ARRoutePaths.AR_CAPITAL_GRANTS} element={<AdyenCapitalGrantsPage/>} />
                    {isCustomerAndSupplier && BillRoutes}
                    <Route path={ARRoutePaths.AR_CUSTOMER_NOTIFICATIONS} element={<CustomerNotificationsPage/>}/>
                    <Route path={ARRoutePaths.AR_SETTINGS} element={<SettingsPage/>}/>
                    <Route path={ARRoutePaths.AR_SUPPLIER_NOTIFICATIONS} element={<SupplierNotificationsPage/>}/>
                    <Route path={ARRoutePaths.AR_BRANDING} element={<BrandingPage/>}/>
                    <Route path={ARRoutePaths.AR_BANKING} element={<BankingPage/>}/>
                    <Route path={ARRoutePaths.AR_INTEGRATION} element={<IntegrationsWrapperPage/>}/>
                </Route>
            </Routes>
        </BrowserRouter>
    </Elements>;
};

const RenderRoutes = (): JSX.Element => {
    const ldClient = useLDClient();
    const authStrategy = UserAuthenticationProvider.getAuthenticateStrategy();

    const { supertokensGlobalEnableDebugLogs } = useFlags<FeatureFlags>();

    setupAxiosAuth();

    initSupertokens(supertokensGlobalEnableDebugLogs);

    const [supertokensLoading, setSupertokensLoading] = useState(true);
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        const checkSession = async () => {
            try {
                const sessionExists = await Session.doesSessionExist();
                console.log('session exists: ', sessionExists);
                if (sessionExists) {
                    const r = await Session.getClaimValue({ claim: UserRoleClaim });
                    setRoles(r);
                }
            } catch (error) {
                console.log('session is invalid', error);
            }
            setSupertokensLoading(false);
        };
        checkSession();
    }, []);

    // this means the auth strategy is set and the user is logged in

    const { supplierLoginData, setSupplierLoginData } = useSupplierStore.getState();
    const customerID = supplierLoginData?.customer_id;
    const { data: supplierMappingData, isLoading } = useGetCustomerSupplierMappings({
        customerID,
    });

    const signUpSupplierId = window.localStorage.getItem(SIGN_UP_SUPPLIER_ID_KEY);
    const supplierIds = supplierMappingData?.suppliers?.length > 0 ? supplierMappingData?.suppliers.map((supplier) => supplier.id) : [];
    const customerSupplierId = signUpSupplierId && supplierIds.includes(signUpSupplierId) ? signUpSupplierId : supplierIds[0];

    // booleans that decide which routes to show to the user
    let isSessionValid = authStrategy && UserAuthenticationProvider.isARLogin();
    let isCustomerLogin = isSessionValid && supplierLoginData?.isCustomer;
    let isSupplierLogin = isSessionValid;

    // override booleans if supertokens session is active
    if (roles?.length > 0) {
        isSessionValid = roles?.length > 0;
        isCustomerLogin = roles?.includes('customer');
        isSupplierLogin = roles?.includes('supplier');
    }

    // We need to skip the cached supplierIDs here since it can change in the drop down
    // however we want to use the sign up supplier id if is attached to this customer
    const supplierID = isCustomerLogin ? customerSupplierId : supplierLoginData?.supplier_id;
    useEffect(() => {
        const resetStoredSupplierId = {
            ...supplierLoginData,
            supplier_id: supplierID,
        };
        setSupplierLoginData(resetStoredSupplierId);
    }, [supplierID]);

    if (supertokensLoading) {
        return <div/>;
    }

    if (isSessionValid) {
        if (isCustomerLogin) {
            // If we're still loading the customer supplier mappings, we don't want to render anything for a customer
            if (isLoading) {
                return <div/>;
            }
            // setup launchdarkly with both customer and supplier context for multi flag evaluation in favor
            // of switching to individual context setup
            setupLaunchDarkly({
                kind: LaunchDarklyContextKind.multi,
                customer: getCustomerContext(customerID),
                arSupplier: getSupplierContext(supplierID),
                loginEmail: getLoginContext(supplierLoginData?.email)
            }, ldClient);
            return <APRoutes/>;
        }

        if (isSupplierLogin) {
            setupLaunchDarkly(getSupplierContext(supplierID), ldClient);
            return <ARRoutes/>;
        }
    }
    return <PublicRoutes/>;
};

const CreateRoutes = () => (
    <MaintenanceMode>
        <RenderRoutes/>
    </MaintenanceMode>
);

export default CreateRoutes;
